import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from './useAuthStore';

type AppRole =
  | 'owner'
  | 'admin'
  | 'investor'
  | 'auditor'
  | 'regulator'
  | 'super_admin'
  | 'observer';

interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  company_id: string | null;
  created_at: string;
}

export function useUserRoles(companyId?: string) {
  const { user } = useAuthStore();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setRoles([]);
      setLoading(false);
      return;
    }

    async function fetchRoles() {
      try {
        setLoading(true);
        let query = supabase.from('user_roles').select('*').eq('user_id', user!.id);

        if (companyId) {
          query = query.or(`company_id.eq.${companyId},company_id.is.null`);
        }

        const { data, error } = await query;

        if (error) throw error;
        setRoles(data || []);
      } catch (error) {
        console.error('Error fetching user roles:', error);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    }

    fetchRoles();
  }, [user, companyId]);

  /**
   * Everything below is memoised on `roles`, and that is not a micro-optimisation.
   *
   * These were plain function expressions, so each render produced fresh
   * identities. `useNDACompliance` lists `hasRole` in its effect's dependency
   * array, which made a loop that could not settle: effect runs -> setState ->
   * re-render -> new `hasRole` -> dependency changed -> effect runs. React caps it
   * with "Maximum update depth exceeded", and the page emitted roughly 1,500
   * console errors in a few minutes and stalled the renderer intermittently.
   *
   * Only one consumer depends on these identities today, but eleven others call
   * them, and the next one to put one in a dependency array would reopen the same
   * hole. Stability belongs here, at the source, not in each caller.
   */
  const hasRole = useCallback(
    (role: AppRole, forCompanyId?: string): boolean => {
      if (!roles.length) return false;

      return roles.some((r) => {
        const roleMatches = r.role === role;

        if (forCompanyId) {
          // Check if user has role for specific company or globally
          return roleMatches && (r.company_id === forCompanyId || r.company_id === null);
        }

        return roleMatches;
      });
    },
    [roles]
  );

  const hasAnyRole = useCallback(
    (rolesList: AppRole[], forCompanyId?: string): boolean => {
      return rolesList.some((role) => hasRole(role, forCompanyId));
    },
    [hasRole]
  );

  // The parameter deliberately shadows the hook's own `companyId`: callers pass
  // the company they are asking about, and omitting it asks globally.
  const isOwner = useCallback((forCompanyId?: string) => hasRole('owner', forCompanyId), [hasRole]);
  const isAdmin = useCallback((forCompanyId?: string) => hasRole('admin', forCompanyId), [hasRole]);
  const isInvestor = useCallback(
    (forCompanyId?: string) => hasRole('investor', forCompanyId),
    [hasRole]
  );
  const isAuditor = useCallback(
    (forCompanyId?: string) => hasRole('auditor', forCompanyId),
    [hasRole]
  );
  const isRegulator = useCallback(
    (forCompanyId?: string) => hasRole('regulator', forCompanyId),
    [hasRole]
  );
  const isSuperAdmin = useCallback(() => hasRole('super_admin'), [hasRole]);
  const isObserver = useCallback(
    (forCompanyId?: string) => hasRole('observer', forCompanyId),
    [hasRole]
  );

  // Memoised too, so a caller can depend on the whole object without looping.
  return useMemo(
    () => ({
      roles,
      loading,
      hasRole,
      hasAnyRole,
      isOwner,
      isAdmin,
      isInvestor,
      isAuditor,
      isRegulator,
      isSuperAdmin,
      isObserver,
    }),
    [
      roles,
      loading,
      hasRole,
      hasAnyRole,
      isOwner,
      isAdmin,
      isInvestor,
      isAuditor,
      isRegulator,
      isSuperAdmin,
      isObserver,
    ]
  );
}
